import { useAuth0 } from "@auth0/auth0-react";
import { Dialog, DialogPanel } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { AnimatePresence, motion } from "framer-motion";
import { LogOut, SquareMenu } from "lucide-react";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom";
import tw from "twin.macro";

import { SquaredLogo } from "@/assets/logos";
import { Button } from "@/components/buttons";
import { Transition } from "@/components/Transition";
import { HasChildren } from "@/types/shared";

import { fadeRouteProps } from "./AnimatedRoute.motion";
import { Navigation } from "./Menu";

const Layout = ({ children }: HasChildren) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { t } = useTranslation();

  const { logout } = useAuth0();
  return (
    <motion.div
      {...fadeRouteProps}
      tw="h-full w-full overflow-x-hidden isolate"
    >
      {/* Mobile sidebar for mobile */}
      <Transition show={sidebarOpen} as={Fragment}>
        <Dialog as="div" tw="relative z-50 lg:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter={tw`transition-opacity ease-linear duration-300`}
            enterFrom={tw`opacity-0`}
            enterTo={tw`opacity-100`}
            leave={tw`transition-opacity ease-linear duration-300`}
            leaveFrom={tw`opacity-100`}
            leaveTo={tw`opacity-0`}
          >
            <div tw="fixed inset-0 bg-gray-900/80" />
          </Transition.Child>

          <div tw="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter={tw`transition ease-in-out duration-300 transform`}
              enterFrom={tw`-translate-x-full`}
              enterTo={tw`translate-x-0`}
              leave={tw`transition ease-in-out duration-300 transform`}
              leaveFrom={tw`translate-x-0`}
              leaveTo={tw`-translate-x-full`}
            >
              <DialogPanel tw="relative mr-16 flex w-full max-w-xs flex-1">
                <Transition.Child
                  as={Fragment}
                  enter={tw`ease-in-out duration-300`}
                  enterFrom={tw`opacity-0`}
                  enterTo={tw`opacity-100`}
                  leave={tw`ease-in-out duration-300`}
                  leaveFrom={tw`opacity-100`}
                  leaveTo={tw`opacity-0`}
                >
                  <div tw="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button
                      type="button"
                      tw="-m-2.5 p-2.5"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span tw="sr-only">Close sidebar</span>
                      <XMarkIcon tw="h-6 w-6 text-white" aria-hidden="true" />
                    </button>
                  </div>
                </Transition.Child>

                {/* Sidebar component, swap this element with another sidebar if you like */}
                <div tw="relative flex grow flex-col gap-y-5 overflow-y-auto px-6 pt-8 pb-4">
                  <div tw="fixed inset-0 p-8 h-screen w-80 bg-primary-800 flex flex-col gap-12 items-center select-none">
                    <div tw="h-56 w-80 absolute top-0 right-0 -translate-y-1/2 rounded-bl-[70%] bg-primary-600/50" />
                    <div tw="h-72 w-80 absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/3 rounded-tr-[70%] bg-primary-900/75" />
                    <SquaredLogo tw="z-50" onPrimary />
                    <div tw="flex flex-col w-full h-full justify-between items-center">
                      <Navigation onNavigate={() => setSidebarOpen(false)} />
                      <Button
                        tw="flex z-50 gap-x-3 items-center p-2 leading-6 font-semibold text-white"
                        onClick={() =>
                          logout({
                            logoutParams: { returnTo: window.location.origin },
                          })
                        }
                      >
                        <LogOut
                          aria-hidden="true"
                          strokeLinecap="round"
                          tw="h-6 w-6 shrink-0 stroke-1.5 text-white "
                        />
                        {t("navigation.logout")}
                      </Button>
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      {/* Static sidebar for desktop */}
      <div tw="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col border-r">
        {/* Sidebar component, swap this element with another sidebar if you like */}
        <div tw="relative flex grow flex-col gap-y-5 overflow-y-auto px-6 pt-8 pb-4">
          <div tw="fixed inset-0 p-8 h-screen w-80 bg-primary-800 flex flex-col gap-12 items-center select-none">
            <div tw="h-56 w-80 absolute top-0 right-0 -translate-y-1/2 rounded-bl-[70%] bg-primary-600/50" />
            <div tw="h-72 w-80 absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/3 rounded-tr-[70%] bg-primary-900/75" />
            <SquaredLogo tw="z-50" onPrimary />
            <div tw="flex flex-col w-full h-full justify-between items-center">
              <Navigation onNavigate={() => setSidebarOpen(false)} />
              <Button
                tw="flex z-50 gap-x-3 items-center p-2 leading-6 font-semibold text-white"
                onClick={() =>
                  logout({ logoutParams: { returnTo: window.location.origin } })
                }
              >
                <LogOut
                  aria-hidden="true"
                  strokeLinecap="round"
                  tw="h-6 w-6 shrink-0 stroke-1.5 text-white "
                />
                {t("navigation.logout")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div tw="lg:pl-72 h-full">
        <div tw="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 px-4 sm:gap-x-6 sm:px-6 lg:px-8 bg-primary-800 lg:hidden">
          <button
            type="button"
            tw="-m-2.5 p-2.5 text-on-background/70"
            onClick={() => setSidebarOpen(true)}
          >
            <span tw="sr-only">Open sidebar</span>
            <SquareMenu tw="h-6 w-6 text-white" aria-hidden="true" />
          </button>
        </div>

        {/* Content */}
        {children ?? (
          <AnimatePresence mode="wait">
            <Outlet />
          </AnimatePresence>
        )}
      </div>
    </motion.div>
  );
};

export default Layout;
